/* eslint-disable max-lines */
import { BulkSelector } from '@core/components/BulkSelector';
import type { BulkSelectableItem } from '@core/components/BulkSelector';
import { Button, Slider } from '@soteria/design-system-ve';
import { useState, useRef, useCallback, useEffect } from 'react';

import * as styles from './LabelWithScoreThresholdSelector.css';
import * as sliderInputStyles from './SliderInputStyles.css';

// Utility function to convert score threshold to human-readable label
const getScoreLabel = (score: number): string => {
  if (score <= 50) {
    return 'Permissive';
  } else if (score <= 70) {
    return 'Moderate';
  } else {
    return 'Strict';
  }
};

// Utility function to get description for score ranges
const getScoreDescription = (score: number): string => {
  if (score <= 50) {
    return 'Catches most potential violations (0-50)';
  } else if (score <= 70) {
    return 'Balanced approach (51-70)';
  } else {
    return 'High-confidence violations only (71-100)';
  }
};

// Helper function to validate numeric input and clamp to valid range
const validateAndClampScore = (value: string, min: number, max: number): number => {
  const numValue = parseInt(value, 10);

  if (isNaN(numValue)) return min;

  return Math.max(min, Math.min(max, numValue));
};

// Inline Editable Value Component
interface InlineEditableValueProps {
  value: number;
  min: number;
  max: number;
  compact?: boolean;
  onChange: (value: number) => void;
  ariaLabel?: string;
}

function InlineEditableValue({ value, min, max, compact = false, onChange, ariaLabel }: InlineEditableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    setIsEditing(true);
    setEditValue(value.toString());
  }, [value]);

  const handleBlur = useCallback(() => {
    const numValue = validateAndClampScore(editValue, min, max);

    onChange(numValue);
    setIsEditing(false);
  }, [editValue, min, max, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBlur();
      } else if (e.key === 'Escape') {
        setEditValue(value.toString());
        setIsEditing(false);
      }
    },
    [handleBlur, value]
  );

  // Auto-focus when switching to edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        className={compact ? sliderInputStyles.compactInlineEditInput : sliderInputStyles.inlineEditInput}
        max={max}
        min={min}
        type="number"
        value={editValue}
        onBlur={handleBlur}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <button
      aria-label={ariaLabel || `Click to edit value ${value}`}
      className={compact ? sliderInputStyles.compactInlineEditableValue : sliderInputStyles.inlineEditableValue}
      type="button"
      onClick={handleClick}
    >
      {value}
    </button>
  );
}

export interface LabelWithScore {
  labelId: string;
  scoreThreshold: number;
}

export interface ScoreLabelOption extends BulkSelectableItem {
  id: string;
  labelName: string;
}

interface LabelWithScoreThresholdSelectorProps {
  availableLabels: ScoreLabelOption[];
  selectedLabels: LabelWithScore[];
  loading?: boolean;
  title?: string;
  emptyStateMessage?: string;
  minScore?: number;
  maxScore?: number;
  defaultScoreThreshold?: number;
  showScoreDescription?: boolean;
  enableBulkSelection?: boolean;
  itemsPerPage?: number;
  onChange: (labels: LabelWithScore[]) => void;
}

// eslint-disable-next-line complexity
export function LabelWithScoreThresholdSelector({
  availableLabels,
  selectedLabels,
  loading,
  title = 'Image Labels',
  emptyStateMessage = 'No image labels selected. Add labels to define score threshold criteria.',
  minScore = 0,
  maxScore = 100,
  defaultScoreThreshold = 70,
  showScoreDescription = true,
  enableBulkSelection = false,
  itemsPerPage = 8,
  onChange,
}: LabelWithScoreThresholdSelectorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [scoreThreshold, setScoreThreshold] = useState(defaultScoreThreshold);

  // Bulk selection state
  const [bulkSelectedLabelIds, setBulkSelectedLabelIds] = useState<string[]>([]);
  const [bulkScoreThreshold, setBulkScoreThreshold] = useState(defaultScoreThreshold);

  const unselectedLabels = availableLabels.filter(label => !selectedLabels.some(selected => selected.labelId === label.id));

  const handleAddLabel = () => {
    if (selectedLabelId && selectedLabelId.trim() && scoreThreshold >= minScore && scoreThreshold <= maxScore) {
      const newLabel: LabelWithScore = {
        labelId: selectedLabelId.trim(),
        scoreThreshold,
      };

      onChange([...selectedLabels, newLabel]);
      setSelectedLabelId('');
      setScoreThreshold(defaultScoreThreshold);
      setShowAddForm(false);
    }
  };

  // Bulk selection handlers

  const handleBulkAddLabels = () => {
    if (bulkSelectedLabelIds.length === 0) return;

    const newLabels: LabelWithScore[] = bulkSelectedLabelIds.map(labelId => ({
      labelId,
      scoreThreshold: bulkScoreThreshold,
    }));

    onChange([...selectedLabels, ...newLabels]);
    setBulkSelectedLabelIds([]);
    setBulkScoreThreshold(defaultScoreThreshold);
    setShowAddForm(false);
  };

  const handleRemoveLabel = (labelId: string) => {
    onChange(selectedLabels.filter(label => label.labelId !== labelId));
  };

  const handleScoreChange = (labelId: string, newScore: number) => {
    onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, scoreThreshold: newScore } : label)));
  };

  const getLabelName = (labelId: string) => availableLabels.find(label => label.id === labelId)?.labelName || labelId;

  const getScoreColor = (): 'success' =>
    // Always return green ('success') color for consistent visual styling
    'success';

  if (loading) {
    return <div className={styles.loading}>Loading labels...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {unselectedLabels.length > 0 && <Button onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Cancel' : 'Add Label(s)'}</Button>}
      </div>

      {/* Add Label Form */}
      {showAddForm && (
        <div className={styles.addForm}>
          {enableBulkSelection ? (
            // Bulk Selection Mode
            <div className={styles.bulkSelectionContainer}>
              <BulkSelector<ScoreLabelOption>
                className={styles.formField}
                emptyPageMessage="No image labels available on this page"
                emptySearchMessage="No labels found matching your search"
                getItemSearchText={item => item.labelName}
                items={unselectedLabels}
                itemsPerPage={itemsPerPage}
                legend="Select Image Labels"
                renderItem={item => item.labelName}
                searchAriaLabel="Search image labels"
                searchPlaceholder="Search labels..."
                selectedItemIds={bulkSelectedLabelIds}
                onSelectedItemsChange={setBulkSelectedLabelIds}
              />

              {bulkSelectedLabelIds.length > 0 && (
                <div className={styles.bulkScoreSection}>
                  <div className={styles.formField}>
                    <div className={sliderInputStyles.sliderInputContainer}>
                      <div className={sliderInputStyles.verticalSliderContainer}>
                        <div className={sliderInputStyles.sliderLabelHeader}>
                          <span className={sliderInputStyles.sliderLabelText}>
                            Default Score Threshold for Selected Labels: {getScoreLabel(bulkScoreThreshold)} ({bulkScoreThreshold})
                          </span>
                          <InlineEditableValue
                            ariaLabel="Score threshold value input"
                            max={maxScore}
                            min={minScore}
                            value={bulkScoreThreshold}
                            onChange={setBulkScoreThreshold}
                          />
                        </div>
                        <div className={sliderInputStyles.sliderWrapper}>
                          <Slider
                            showMinMaxLabels
                            aria-describedby={showScoreDescription ? 'bulk-score-description' : undefined}
                            color={getScoreColor()}
                            id="bulkScoreSlider"
                            label=""
                            max={maxScore}
                            min={minScore}
                            step={1}
                            subLabel={showScoreDescription ? getScoreDescription(bulkScoreThreshold) : undefined}
                            value={bulkScoreThreshold}
                            onChange={setBulkScoreThreshold}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.formActions}>
                <Button disabled={bulkSelectedLabelIds.length === 0} onClick={handleBulkAddLabels}>
                  Add Selected Labels ({bulkSelectedLabelIds.length})
                </Button>
                <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            // Single Selection Mode (existing functionality)
            <>
              <div className={styles.formField}>
                <label className={styles.fieldLabel} htmlFor="labelSelect">
                  Select Image Label
                </label>
                <select className={styles.select} id="labelSelect" value={selectedLabelId} onChange={e => setSelectedLabelId(e.target.value)}>
                  <option value="">Choose an image label...</option>
                  {unselectedLabels.map(label => (
                    <option key={label.id} value={label.id}>
                      {label.labelName}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formField}>
                <div className={sliderInputStyles.sliderInputContainer}>
                  <div className={sliderInputStyles.verticalSliderContainer}>
                    <div className={sliderInputStyles.sliderLabelHeader}>
                      <span className={sliderInputStyles.sliderLabelText}>
                        Score Threshold: {getScoreLabel(scoreThreshold)} ({scoreThreshold})
                      </span>
                      <InlineEditableValue
                        ariaLabel="Score threshold value input"
                        max={maxScore}
                        min={minScore}
                        value={scoreThreshold}
                        onChange={setScoreThreshold}
                      />
                    </div>
                    <div className={sliderInputStyles.sliderWrapper}>
                      <Slider
                        showMinMaxLabels
                        aria-describedby={showScoreDescription ? 'score-description' : undefined}
                        color={getScoreColor()}
                        id="scoreSlider"
                        label=""
                        max={maxScore}
                        min={minScore}
                        step={1}
                        subLabel={showScoreDescription ? getScoreDescription(scoreThreshold) : undefined}
                        value={scoreThreshold}
                        onChange={setScoreThreshold}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <Button disabled={!selectedLabelId || !selectedLabelId.trim()} onClick={handleAddLabel}>
                  Add Image Label
                </Button>
                <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Selected Labels List */}
      {selectedLabels.length > 0 && (
        <div>
          <div className={styles.sectionTitle}>Selected Image Labels ({selectedLabels.length})</div>
          <div className={styles.labelsList}>
            {selectedLabels.map(label => (
              <div key={label.labelId} className={styles.labelItem}>
                <div className={styles.labelInfo}>
                  <div className={styles.labelName}>{getLabelName(label.labelId)}</div>
                  <div className={styles.labelScore}>
                    Score threshold: {getScoreLabel(label.scoreThreshold)} ({label.scoreThreshold})
                  </div>
                  {showScoreDescription && <div className={styles.labelScoreDesc}>{getScoreDescription(label.scoreThreshold)}</div>}
                </div>
                <div className={styles.labelControls}>
                  <div className={sliderInputStyles.compactSliderInputRow}>
                    <div className={sliderInputStyles.compactSliderWrapper}>
                      <Slider
                        aria-describedby={showScoreDescription ? `score-desc-${label.labelId}` : undefined}
                        aria-label={`Score threshold for ${getLabelName(label.labelId)}: ${getScoreLabel(label.scoreThreshold)} (${label.scoreThreshold} out of ${maxScore})`}
                        color={getScoreColor()}
                        max={maxScore}
                        min={minScore}
                        showMinMaxLabels={false}
                        size="small"
                        step={1}
                        value={label.scoreThreshold}
                        onChange={newValue => handleScoreChange(label.labelId, newValue)}
                      />
                    </div>
                    <InlineEditableValue
                      compact
                      ariaLabel={`Score threshold value input for ${getLabelName(label.labelId)}`}
                      max={maxScore}
                      min={minScore}
                      value={label.scoreThreshold}
                      onChange={newValue => handleScoreChange(label.labelId, newValue)}
                    />
                  </div>
                  <Button aria-label={`Remove ${getLabelName(label.labelId)} label`} onClick={() => handleRemoveLabel(label.labelId)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedLabels.length === 0 && (
        <div aria-live="polite" className={styles.emptyState} role="status">
          {emptyStateMessage}
        </div>
      )}
    </div>
  );
}
